import React, { useEffect, useState } from "react";
import { MentionEditor } from "./Editor";
import { ThreadCard } from "./ThreadCard";
import { ChevronDown, PenLine } from "lucide-react";
import profilePicture from "../../../assets/icons/perfilionvo.png";
import {
  editComment,
  editThreadForm,
  handleDeleteComment,
  handleDeleteThread,
  handleSubmitComment,
  handleSubmitLikes,
  handleSubmitThread,
  putAnchor,
  putSeen,
} from "../../../utils/ApiCall";
import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";

export function ThreadList({
  threads,
  setThreads,
  backendUrl,
  currentUserId,
  currentUserEmail,
  currentUserUsername,
  pathname,
  threadKey,
  row,
  avatar,
  initiatorValue,
  setNumberOfThreads,
}) {
  const [showEditor, setShowEditor] = React.useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const location = useLocation();
  const board = location?.pathname?.slice(1);
  const handleFormSubmitThread = async (
    content,
    files,
    selectedUser,
    selectedGroup
  ) => {
    setIsUploading(true);
    const normalizedUsers = Array.isArray(selectedUser)
      ? selectedUser.flat()
      : [];
    try {
      const response = await handleSubmitThread(
        pathname,
        threadKey,
        files,
        row,
        "",
        content,
        normalizedUsers,
        selectedGroup
      );

      if (response && response.data) {
        let rowThread = response.data;
        let thread = rowThread.threads;

        setThreads(thread);
        setNumberOfThreads(thread.length);
        setIsUploading(false);
      }
    } catch (error) {
      console.error("Error al enviar el thread:", error);
    } finally {
      setShowEditor(false);
    }
  };

  const handleFormSubmitLike = async (thread, comment) => {
    const response = await handleSubmitLikes(pathname, thread, comment, row);
    setThreads(response);
  };

  const handleFormSubmitComment = async (
    id,
    content,
    files,
    selectedUsers,
    selectedGroup
  ) => {
    const response = await handleSubmitComment(
      pathname,
      id,
      threadKey,
      files,
      row,
      content,
      selectedUsers,
      selectedGroup
    );

    if (response && response.data && response.data.threads) {
      const { threads } = response.data;
      setThreads(threads);
    }
  };

  const handleEditThread = async (threadId, content, files, removedFiles) => {
    const editedThread = await editThreadForm(
      threadKey,
      threadId,
      content,
      board,
      row.id,
      files,
      [] // state.media
    );

    if (editedThread?.data?.threads) {
      const updatedThread = editedThread.data.threads.find(
        (thread) => thread.id === threadId
      );

      setThreads((prevThreads) =>
        prevThreads.map((thread) => {
          if (thread.id === threadId) {
            const currentMedia = thread.media || [];
            const remainingMedia = currentMedia.filter(
              (media) => !removedFiles.some((file) => file.id === media.id)
            );
            return {
              ...thread,
              content: updatedThread?.content ?? content,
              media: remainingMedia.concat(updatedThread?.media ?? []),
            };
          }
          return thread;
        })
      );
    } else {
      console.error("No se encontraron threads actualizados en la respuesta");
    }
  };

  const handleEditComment = async (
    threadId,
    commentId,
    content,
    files,
    removedFiles
  ) => {
    const editedComment = await editComment(
      threadKey,
      commentId,
      content,
      board,
      row.id,
      files,
      [] // state.media
    );

    if (editedComment?.data?.threads) {
      const updatedThread = editedComment.data.threads.find(
        (thread) => thread.id === threadId
      );

      if (!updatedThread) {
        console.error("No se encontró el thread actualizado en la respuesta");
        return;
      }

      const updatedComment = updatedThread.comments.find(
        (comment) => comment.id === commentId
      );

      if (!updatedComment) {
        console.error(
          "No se encontró el comentario actualizado en la respuesta"
        );
        return;
      }

      setThreads((prevThreads) =>
        prevThreads.map((thread) => {
          if (thread.id === threadId) {
            return {
              ...thread,
              comments: thread.comments.map((comment) => {
                if (comment.id === commentId) {
                  const currentMedia = comment.media || [];
                  const remainingMedia = currentMedia.filter(
                    (media) =>
                      !removedFiles.some((file) => file.id === media.id)
                  );

                  return {
                    ...comment,
                    content: updatedComment?.content ?? content,
                    media: remainingMedia.concat(updatedComment?.media ?? []),
                  };
                }
                return comment;
              }),
            };
          }
          return thread;
        })
      );
    } else {
      console.error("No se encontraron threads actualizados en la respuesta");
    }
  };

  const handleToggleAnchor = async (thread) => {
    const response = await putAnchor(
      thread.anchored,
      thread.id,
      pathname,
      row.id
    );

    if (response && response.data && response.data.threads) {
      const { threads } = response.data;
      setThreads(threads);
    }
  };

  const handleSeen = async (message, messageType) => {
    let usersMails = await message.seen_by?.map((e) => e.email);
    if (!usersMails.includes(localStorage.email)) {
      const response = await putSeen(message.id, pathname, messageType, row.id);
      if (response && response.data && response.data.threads) {
        const { threads } = response.data;
        setThreads(threads);
      }
    }
  };

  const handleFormDeleteThread = async (thread) => {
    Swal.fire({
      title: "¿Está seguro que desea eliminar este thread?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const response = await handleDeleteThread(pathname, thread, row);
        let newThreads = response.data.threads;

        setThreads(newThreads);
        setNumberOfThreads(newThreads.length);

        Swal.fire("Tu thread ha sido eliminado", "", "success");
      }
    });
  };

  const handleFormDeleteComment = async (commentId) => {
    Swal.fire({
      title: "¿Está seguro que desea eliminar este comentario?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const response = await handleDeleteComment(pathname, commentId, row);
        let newThreads = response.data.threads;

        setThreads(newThreads);

        Swal.fire("El comentario ha sido eliminado", "", "success");
      }
    });
  };

  useEffect(() => {
    if (initiatorValue) {
      const element = document.getElementById(initiatorValue);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [initiatorValue, threads]);

  return (
    <div className="max-w-2xl mx-auto py-8">
      <button
        onClick={() => setShowEditor(!showEditor)}
        className={`w-full mb-6 flex items-center justify-between px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
          showEditor
            ? "bg-blue-50 border-blue-500 text-blue-700"
            : "bg-white border-gray-200 text-gray-700 hover:border-blue-300 hover:bg-blue-50/50"
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
            <img
              src={avatar ? `${backendUrl}${avatar}` : profilePicture}
              alt="foto"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex items-center gap-2">
            <PenLine
              size={18}
              className={showEditor ? "text-blue-500" : "text-gray-400"}
            />
            <span className="font-semibold text-lg">
              Empezar nueva conversación
            </span>
          </div>
        </div>
        <ChevronDown
          size={20}
          className={`text-gray-400 transition-transform duration-200 ease-in-out ${
            showEditor ? "rotate-180 text-blue-500" : ""
          }`}
        />
      </button>

      {showEditor && (
        <div className="mb-6  rounded-lg">
          <MentionEditor
            onPublish={handleFormSubmitThread}
            backendUrl={backendUrl}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            isUploading={isUploading}
          />
        </div>
      )}

      {threads.map((thread) => (
        <ThreadCard
          backendUrl={backendUrl}
          key={thread.id}
          thread={thread}
          comments={thread.comments}
          currentUserId={currentUserId}
          currentUserEmail={currentUserEmail}
          currentUserUsername={currentUserUsername}
          profilePicture={profilePicture}
          onComment={handleFormSubmitComment}
          onEditThread={handleEditThread}
          onEditComment={handleEditComment}
          onToggleAnchor={handleToggleAnchor}
          // onView={handleThreadView}
          // onCommentView={handleCommentView}
          onLike={handleFormSubmitLike}
          onCommentLike={handleFormSubmitLike}
          avatar={avatar}
          handleSeen={handleSeen}
          onDeleteThread={handleFormDeleteThread}
          onDeleteComment={handleFormDeleteComment}
          setIsEditing={setIsEditing}
          isEditing={isEditing}
        />
      ))}
    </div>
  );
}
